import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Skeleton, SkeletonGroup } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/utils'
import { type AutoLiabilityEstimate, useAutoLiabilityEstimate } from '@/models/autoLiabilityEstimate'

export function EstimateDialog({
  dotNumber,
  carrier,
  powerUnits,
}: {
  dotNumber: string
  carrier: string
  powerUnits?: number
}) {
  const [open, setOpen] = useState(false)
  // Only fetch while the dialog is open — passing undefined disables the query.
  // Scope the estimate to this application's fleet size, not the DOT's FMCSA fleet.
  const estimateQuery = useAutoLiabilityEstimate(open ? dotNumber : undefined, powerUnits)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Get estimate
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Estimate — {carrier}</DialogTitle>
          <DialogDescription>Live estimate — not saved.</DialogDescription>
        </DialogHeader>

        {estimateQuery.isLoading ? (
          <SkeletonGroup className="grid grid-cols-3 gap-6 py-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-20 w-full" />
            ))}
          </SkeletonGroup>
        ) : estimateQuery.isError ? (
          <div className="text-muted-foreground py-6 text-sm">
            {estimateQuery.error.response?.data.detail ||
              (estimateQuery.error.response?.status === 404
                ? `No CAB record found for DOT ${dotNumber}.`
                : 'Failed to compute an estimate for this DOT.')}
          </div>
        ) : estimateQuery.data ? (
          <EstimateContent estimate={estimateQuery.data} />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function EstimateContent({ estimate }: { estimate: AutoLiabilityEstimate }) {
  const limitLabel = `$${(estimate.requestedLimit / 1_000_000).toFixed(0)}M limit`
  return (
    <div className="flex flex-col gap-2">
      <div className="text-muted-foreground text-sm">
        Auto liability · {estimate.powerUnits} {estimate.powerUnits === 1 ? 'unit' : 'units'} · {limitLabel}
      </div>
      <div className="grid grid-cols-3 gap-6">
        <RangeCell label="Low" total={estimate.totalLow} perUnit={estimate.perUnitLow} />
        <RangeCell label="Midpoint" total={estimate.totalMid} perUnit={estimate.perUnitMid} emphasized />
        <RangeCell label="High" total={estimate.totalHigh} perUnit={estimate.perUnitHigh} />
      </div>
    </div>
  )
}

function RangeCell({
  label,
  total,
  perUnit,
  emphasized,
}: {
  label: string
  total: number
  perUnit: number
  emphasized?: boolean
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{label}</span>
      <span
        className={
          emphasized
            ? 'text-general-foreground text-2xl font-semibold'
            : 'text-general-foreground text-lg font-semibold'
        }
      >
        {formatCurrency(total)}
      </span>
      <span className="text-muted-foreground text-xs">{formatCurrency(perUnit)} / unit</span>
    </div>
  )
}
